import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, Play } from "lucide-react";
import sourcePoster from "../../assets/how-it-works/source.jpg";
import type { StartClipping } from "./HeroSection";

interface CreateMillionsSectionProps {
  onStartClipping: StartClipping;
}

const YOUTUBE_EMBED_URL =
  "https://www.youtube.com/embed/9IQ_ldV9z_A?start=3279&end=3330&autoplay=0&mute=1&controls=1&enablejsapi=1&playsinline=1&rel=0";

const youtubeCommand = (iframe: HTMLIFrameElement | null, command: "playVideo" | "pauseVideo") => {
  iframe?.contentWindow?.postMessage(
    JSON.stringify({ event: "command", func: command, args: [] }),
    "https://www.youtube.com"
  );
};

export const CreateMillionsSection: React.FC<CreateMillionsSectionProps> = ({
  onStartClipping,
}) => {
  const sectionRef = useRef<HTMLElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [shouldMount, setShouldMount] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [documentVisible, setDocumentVisible] = useState(
    typeof document === "undefined" || document.visibilityState === "visible"
  );

  useEffect(() => {
    const element = sectionRef.current;
    if (!element || typeof IntersectionObserver === "undefined") {
      setShouldMount(true);
      setIsVisible(true);
      return;
    }
    const mountObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setShouldMount(true);
      },
      { rootMargin: "500px 0px", threshold: 0 }
    );
    const playbackObserver = new IntersectionObserver(
      ([entry]) =>
        setIsVisible(entry.isIntersecting && entry.intersectionRatio >= 0.55),
      { threshold: [0, 0.55, 1] }
    );
    mountObserver.observe(element);
    playbackObserver.observe(element);
    return () => {
      mountObserver.disconnect();
      playbackObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const onVisibilityChange = () =>
      setDocumentVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  useEffect(() => {
    youtubeCommand(
      iframeRef.current,
      isVisible && documentVisible ? "playVideo" : "pauseVideo"
    );
  }, [documentVisible, isVisible]);

  return (
    <section
      ref={sectionRef}
      id="creator-example"
      className="bg-white px-5 py-20 sm:px-6 sm:py-28 lg:px-8"
      aria-labelledby="creator-example-heading"
    >
      <div className="mx-auto max-w-6xl">
        <div className="mx-auto max-w-3xl text-center">
          <h2
            id="creator-example-heading"
            className="tc-section-title text-[var(--tc-ink)]"
          >
            Create content that{" "}
            <span className="text-[var(--tc-blue)]">reaches millions</span>
          </h2>
          <p className="mx-auto mt-5 max-w-[46rem] text-balance text-lg leading-8 text-[var(--tc-ink-muted)]">
            Long videos are full of short-form moments. TopClip finds the
            strongest ones and turns them into clips ready to post.
          </p>
        </div>

        <div className="mx-auto mt-12 max-w-5xl overflow-hidden rounded-[2rem] border border-slate-200 bg-[#0b1220] shadow-[0_30px_80px_rgba(15,23,42,0.20)]">
          <div className="relative aspect-video">
            <img
              src={sourcePoster}
              alt=""
              aria-hidden="true"
              data-testid="creator-video-poster"
              loading="eager"
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${
                iframeLoaded ? "pointer-events-none opacity-0" : "opacity-100"
              }`}
            />
            <div
              aria-hidden="true"
              data-testid="creator-video-poster-overlay"
              className={`pointer-events-none absolute inset-0 z-10 grid place-items-center bg-slate-950/20 transition-opacity duration-500 ${
                iframeLoaded ? "opacity-0" : "opacity-100"
              }`}
            >
              <div className="grid h-16 w-16 place-items-center rounded-full border border-white/30 bg-white/10 text-white">
                <Play aria-hidden="true" className="ml-1 h-7 w-7" />
              </div>
            </div>
            {shouldMount && (
              <iframe
                ref={iframeRef}
                className={`absolute inset-0 h-full w-full transition-opacity duration-500 ${
                  iframeLoaded
                    ? "opacity-100"
                    : "pointer-events-none opacity-0"
                }`}
                src={YOUTUBE_EMBED_URL}
                title="MrBeast long-form source example"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                onLoad={() => {
                  setIframeLoaded(true);
                  youtubeCommand(
                    iframeRef.current,
                    isVisible && documentVisible ? "playVideo" : "pauseVideo"
                  );
                }}
                allowFullScreen
              />
            )}
          </div>
        </div>

        <div className="mt-10 text-center">
          <button
            type="button"
            onClick={() => void onStartClipping()}
            className="tc-primary-action inline-flex min-h-14 items-center justify-center gap-2 rounded-full bg-[var(--tc-blue)] px-9 text-lg font-bold text-white shadow-[var(--tc-shadow-cta)] transition hover:-translate-y-0.5 hover:bg-[var(--tc-blue-hover)] hover:shadow-[0_18px_38px_rgba(22,119,255,0.34)]"
          >
            Try TopClip Now
            <ArrowRight aria-hidden="true" className="h-6 w-6" />
          </button>
        </div>
      </div>
    </section>
  );
};
